import { Client, xml } from "@xmpp/client"
import { getAllItems, publish, retract } from "./PubSub"
import { Element } from "@xmpp/xml"
import { PartialMessage } from "../../reducers/pinnedSlice"
import { MessageType, Message } from "../../reducers/messagesSlice"
import { wizardJid } from "../../jidUtils"
import XmppPubSubApi from "../xmppPubSubApi"
import { captureError } from "../../ErrorHandlers"
import { convert as convertToXML } from "../../lib/messageToXml"
import MessageParser from "../../lib/messageParser"

export type PinnedMessage = {
  itemId: string
  at: number
  ttl: number
  message: PartialMessage
}

const NODE = "urn:slickshift:message-pinning"

export const retrieveAllPinnedMessages = async (
  xmpp: Client,
): Promise<PinnedMessage[]> => {
  const publishToJid = wizardJid(xmpp.jid!)

  const pinnedMessages = await getAllItems({
    xmpp,
    jid: publishToJid,
    node: NODE,
    timeout: 4000,
  })

  const items = pinnedMessages
    .getChild("pubsub")
    ?.getChild("items")
    ?.getChildren("item") as [Element]

  return items.map((item) => {
    const pinned = item.getChild("pinned")

    const message = pinned?.getChild("message") as Element
    const body = message?.getChild("body") as Element
    const messageId = message.getChild("stanza-id") as Element
    const at = pinned?.getChild("at") as Element
    const ttl = pinned?.getChild("ttl") as Element
    return {
      itemId: item.attr("id") as string,
      message: {
        id: messageId.attr("id") as string,
        from: message.attr("from") as string,
        to: message.attr("to") as string,
        type: message.attr("type") as MessageType,
        body: body.getText() as string,
        url: MessageParser.url(message),
      },
      at: new Date(at.getText()).getTime(),
      ttl: parseInt(ttl.getText(), 10),
    }
  })
}

const getLocalISOString = (date: Date): string => {
  const offset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - offset).toISOString().slice(0, -1)
}

const PINNED_MESSAGE_DEFAULT_TTL = "172800"

export const addPinnedMessage = async (
  xmpp: Client,
  message: Message,
  ttl: string = PINNED_MESSAGE_DEFAULT_TTL,
): Promise<void> => {
  const pinned = xml(
    "pinned",
    {
      xmlns: NODE,
    },
    xml("at", {}, getLocalISOString(new Date())),
    xml("ttl", {}, ttl),
    convertToXML(message),
  )
  const publishToJid = wizardJid(xmpp.jid!)

  await publish({
    xmpp,
    jid: publishToJid,
    node: NODE,
    payload: pinned,
    timeout: 4000,
  })
}

export const removePinnedMessage = async (
  xmpp: Client,
  id: string,
): Promise<void> => {
  const publishToJid = wizardJid(xmpp.jid!)

  await retract({ xmpp, jid: publishToJid, node: NODE, id })
}

export const subscribeToPinnedMessages = async (client: Client) => {
  try {
    await XmppPubSubApi.subscribe(
      client,
      wizardJid(client.jid!),
      "urn:slickshift:message-pinning",
    )
  } catch (error: any) {
    if (error.condition !== "item-not-found") {
      captureError(error, {
        origin: "XMPPOnOnline",
        extra: { message: "subscribeToPinnedMessages" },
      })
    }
  }
}
